// =====================================
// NewItems.jsx - VERSIÓN CORREGIDA
// =====================================

import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";

// Hook unificado para la lógica del carrito
import { useNewItemsLogic } from "../../hooks/useNewItemsLogic";

// Componentes de la interfaz y navegación
import Navbar from "../navigation/Navbar";
import AccessModal from "../auth/AccessModal";
import LoadingScreen from "../ui/LoadingScreen";
import { WaterBottleCard } from "./ProductPages/WaterBottleCard";
import DescriptionBox from "./NewItemsComponents/DescriptionBox";
import OrderSummary from "./NewItemsComponents/OrderSummary";
import TotalBar from "./NewItemsComponents/TotalBar";
import DeliveryDateSelector from "./NewItemsComponents/DeliveryDateSelector";
import DispenserCard from "./ProductPages/DispenserCard";
import { DeliverySection } from "./QuickAddressComponents/DeliverySection";

const NewItems = ({ showBottles = true, showDispensers = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // HOOK LÓGICO CENTRALIZADO
  const {
    currentStep,
    currentStepIndex,
    availableSteps,
    isStockLoaded,
    isUpdatingStock,
    hasEnabledProducts,
    stockError,
    refreshStock,
    handleNext,
    handleBack,
    isButtonDisabled,
    getButtonText,
    accessModalOpen,
    setAccessModalOpen,
    isSubmitting,
    newOrderTotal,
    isCoordsLoading,
    progress,
    STEP_CONFIG,
    newSalesTerms
    // Removemos goToStep y setCurrentStepIndex que no existen
  } = useNewItemsLogic({ showBottles, showDispensers });

  // Efecto para manejar el retorno desde OrderProcessing
  useEffect(() => {
    if (location.state?.returnToSummary) {
      // Como no tenemos goToStep, simplemente mostramos el toast
      // El usuario puede navegar manualmente o podemos agregar lógica adicional

      if (location.state?.errorMessage) {
        toast.error(location.state.errorMessage, {
          duration: 5000,
          position: "top-center",
        });
      }
    }
  }, [location.state]);

  // Resto del componente permanece igual...
  // (StockAlert, renderStepContent, etc.)

  // Alerta para mostrar errores de stock o productos no disponibles
  const StockAlert = () => {
    if (!stockError && hasEnabledProducts) return null;

    const message = stockError
      ? `❌ Error cargando inventario: ${stockError}`
      : !hasEnabledProducts
      ? "⚠️ No hay productos habilitados o sin stock disponible"
      : "⚠️ No hay productos disponibles temporalmente";

    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="mx-6 xl:mx-0 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-yellow-600" />
          <p className="text-sm text-yellow-800">{message}</p>
          <button
            onClick={refreshStock}
            className="ml-auto text-yellow-600 hover:text-yellow-800"
            disabled={isUpdatingStock}
          >
            <RefreshCw
              size={14}
              className={isUpdatingStock ? "animate-spin" : ""}
            />
          </button>
        </div>
      </motion.div>
    );
  };

  // Renderiza el contenido del paso actual
  const renderStepContent = () => {
    const stepNumber = currentStepIndex + 1;

    switch (currentStep) {
      case STEP_CONFIG.BOTTLES:
        if (!showBottles) return null;
        return (
          <motion.div
            key="bottles"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-grow pb-40"
          >
            <WaterBottleCard />
          </motion.div>
        );

      case STEP_CONFIG.DISPENSERS:
        if (!showDispensers) return null;
        return (
          <motion.div
            key="dispensers"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-grow pb-40"
          >
            <DescriptionBox
              type="dispensers"
              content={{
                number: stepNumber.toString(),
                title: "Dispensadores",
                description: "Elige los dispensadores necesarios.",
                className: "mt-0 mb-4",
              }}
              showBackButton={currentStepIndex > 0}
              onBack={handleBack}
            />
            <DispenserCard />
          </motion.div>
        );

      case STEP_CONFIG.DELIVERY:
        return (
          <motion.div
            key="delivery"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-grow pb-40"
          >
            <DescriptionBox
              type="delivery"
              content={{
                number: stepNumber.toString(),
                title: "Información de Entrega",
                description: "Selecciona o añade una dirección de entrega",
                className: "mt-0 mb-4",
              }}
              showBackButton={currentStepIndex > 0}
              onBack={handleBack}
            />
            <DeliverySection setAccessModalOpen={setAccessModalOpen} />
          </motion.div>
        );

      case STEP_CONFIG.SCHEDULE:
        return (
          <motion.div
            key="schedule"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-grow overflow-visible pb-40"
          >
            <DescriptionBox
              type="delivery-date"
              content={{
                number: stepNumber.toString(),
                title: "Fecha y franja de entrega",
                description: "Selecciona cuándo quieres recibir tu pedido",
                className: "mt-0 mb-4",
              }}
              showBackButton={currentStepIndex > 0}
              onBack={handleBack}
            />
            <DeliveryDateSelector />
          </motion.div>
        );

      case STEP_CONFIG.SUMMARY:
        return (
          <motion.div
            key="summary"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-grow pb-40"
          >
            <DescriptionBox
              type="summary"
              content={{
                number: stepNumber.toString(),
                title: "Resumen de tu pedido",
                description: "Verifica tu orden y los términos.",
                className: "mt-0 mb-4",
              }}
              showBackButton={currentStepIndex > 0}
              onBack={handleBack}
            />
            <OrderSummary />
          </motion.div>
        );

      default:
        return null;
    }
  };

  // Pantalla de carga
  if (!isStockLoaded) {
    return <LoadingScreen message="Cargando inventario..." />;
  }

  // Pantalla si no hay productos disponibles
  if (!hasEnabledProducts) {
    return (
      <div className="flex flex-col h-full items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle size={48} className="text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-800 mb-2">
            Productos no disponibles
          </h2>
          <p className="text-gray-600 mb-6">
            Los productos seleccionados no están disponibles o no hay stock.
          </p>
          <div className="space-y-3">
            <button
              onClick={refreshStock}
              disabled={isUpdatingStock}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                className={isUpdatingStock ? "animate-spin" : ""}
              />
              {isUpdatingStock ? "Verificando..." : "Verificar disponibilidad"}
            </button>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Renderizado principal
  return (
    <div className="flex flex-col h-screen bg-white">
      <div className="sticky top-0 z-50 bg-white">
        <Navbar type="new-order" staticPosition={true} />
      </div>
      <div className="flex-1 justify-center items-center flex w-full overflow-hidden">
        <div className="flex flex-col h-full max-w-6xl relative w-full">
          <div className="flex-grow overflow-y-auto pt-12 px-4 xl:px-0 no-scrollbar">
            <StockAlert />
            <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
          </div>
          <TotalBar
            currentStepIndex={currentStepIndex}
            totalSteps={availableSteps.length}
            handleNext={handleNext}
            isButtonDisabled={isButtonDisabled}
            getButtonText={getButtonText}
            isSubmitting={isSubmitting}
            isUpdatingStock={isUpdatingStock}
            isCoordsLoading={isCoordsLoading}
            newOrderTotal={newOrderTotal}
            progress={progress}
            termsAccepted={newSalesTerms} // ← Agregar esta línea
          />
          <AccessModal
            open={accessModalOpen}
            onClose={() => setAccessModalOpen(false)}
            initialTab="signin"
            redirectAfterLogin={false}
          />
        </div>
      </div>
    </div>
  );
};

export default NewItems;
